import { gql, useQuery } from "@apollo/client";
import type { Promotions } from "@shared/graphql.types";
import {
  PromoSection,
  AlertProvider,
  AlertProvided
} from "@shared/ui.components";
import { dispatch } from "@shared/utils.events";
import { useUser } from "lib/user";
import { toPromoSectionProps } from "./toPromoSectionProps";
import { carouselTracking } from "./promotionsCarouselTracking";
import { PROMOTIONS_FRAGMENT } from "@shared/graphql.fragments";
import { withErrorBoundary } from "react-error-boundary";
import { usePromoMessage } from "./hooks";
import { useEffect } from "react";
import { isExperimentViewedTracking } from "tracking";
import { trackExperimentViewedEvent } from "tracking/hooks/useExperimentViewed";

const PDP_PROMOTIONS = gql`
  query pdpPromotions($basketId: String, $placementPage: PromoPlacementPage) {
    pdpPromotions(basketId: $basketId, placementPage: $placementPage) {
      ...PromotionsFragment
    }
  }
  ${PROMOTIONS_FRAGMENT}
`;

const PromotionSectionContent = () => {
  const user = useUser();
  const handleMessage = usePromoMessage();

  const {
    loading,
    error,
    data: promotionsSection
  } = useQuery<{ pdpPromotions: Promotions }>(PDP_PROMOTIONS, {
    variables: {
      placementPage: "PDP",
      ...(user.basketId ? { basketId: user.basketId } : {})
    }
  });

  useEffect(() => {
    const experimentViewedEvent =
      promotionsSection?.pdpPromotions.tracking?.viewed?.find(
        isExperimentViewedTracking
      );
    if (experimentViewedEvent) {
      trackExperimentViewedEvent(experimentViewedEvent);
    }
  }, [promotionsSection?.pdpPromotions.tracking?.viewed]);

  if (loading) return null;

  if (error) {
    // eslint-disable-next-line no-console
    console.log("Error getting promotions:", error);
    return null;
  }

  const promoSectionProps = toPromoSectionProps({
    promotions: promotionsSection?.pdpPromotions,
    basketId: user.basketId,
    onMessage: handleMessage
  });
  return (
    <PromoSection
      title={promoSectionProps.title}
      description={promoSectionProps.description}
      promos={promoSectionProps.promos}
      variant={promoSectionProps.variant}
      carouselTracking={carouselTracking()}
    />
  );
};

export const PromotionSectionWrapperComponent = () => {
  return (
    <AlertProvider>
      <AlertProvided trackingLocation="promo-section" trackFn={dispatch} />
      <PromotionSectionContent />
    </AlertProvider>
  );
};

export const PromotionSectionWrapper = withErrorBoundary(
  PromotionSectionWrapperComponent,
  {
    fallback: <></>,
    onError(error) {
      console.warn("Error rendering PromotionSectionWrapper:", error);
    }
  }
);
